import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { Loader2, Mic, Square, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor } from "@/i18n/locale";
import { useVoiceInputWhisper, type WhisperOwnerGuard } from "@/hooks/useVoiceInputWhisper";
import { supabase } from "@/integrations/supabase/client";
import { createMirrorAuthSessionScope, type MirrorAuthLease } from "@/lib/mirrorAuthSession";
import { appAudioFocus } from "@/lib/appAudioFocus";
import { parseWorkoutSetVoice, type WorkoutSetVoiceDraft } from "@/lib/fitness/workoutSetVoice";

interface Props {
  enabled: boolean;
  scopeId: string;
  timed: boolean;
  ownerGuard: WhisperOwnerGuard | null;
  onApply: (draft: WorkoutSetVoiceDraft) => void;
}
type Phase = "idle" | "starting" | "recording" | "transcribing" | "review";
interface CaptureRun { identity: string; auth: MirrorAuthLease; guard: WhisperOwnerGuard; cancelled: boolean; stopping: boolean }

/** Dictation edits a local draft only. The existing workout action records sets. */
export default function WorkoutSetVoiceInput(props: Props) {
  const { lang, t, tf } = useLanguage();
  const voice = useVoiceInputWhisper(lang);
  const voiceRef = useRef(voice); voiceRef.current = voice;
  const identity = JSON.stringify([props.scopeId, props.timed, lang, props.ownerGuard?.ownerId, props.ownerGuard?.authSessionId]);
  const latest = useRef({ props, identity, lang }); latest.current = { props, identity, lang };
  const authScope = useRef(createMirrorAuthSessionScope());
  const [authRevision, setAuthRevision] = useState(0);
  const authKnown = authRevision > 0;
  const mounted = useRef(false), runRef = useRef<CaptureRun | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [draft, setDraft] = useState<WorkoutSetVoiceDraft | null>(null);
  const [message, setMessage] = useState<"notUnderstood" | "error" | null>(null);
  const blocker = useSyncExternalStore(appAudioFocus.subscribe, appAudioFocus.blocking, () => null);
  const cancel = useCallback(() => {
    const run = runRef.current; runRef.current = null;
    if (run) { run.cancelled = true; voiceRef.current.cancelListening(run.guard); }
    if (mounted.current) { setPhase("idle"); setDraft(null); setMessage(null); }
  }, []);
  const current = useCallback((run: CaptureRun) => mounted.current && runRef.current === run
    && !run.cancelled && run.identity === latest.current.identity && latest.current.props.enabled
    && document.visibilityState !== "hidden" && authScope.current.isCurrent(run.auth)
    && !!latest.current.props.ownerGuard?.isCurrent(), []);

  useEffect(() => {
    mounted.current = true;
    const stopWhenHidden = () => { if (document.visibilityState === "hidden") cancel(); };
    document.addEventListener("visibilitychange", stopWhenHidden);
    window.addEventListener("pagehide", cancel);
    const unsubscribeFocus = appAudioFocus.subscribe(() => {
      const priority = appAudioFocus.blocking();
      if (priority && priority !== "capture") cancel();
    });
    const auth = authScope.current;
    const update = (session: unknown) => {
      if (!mounted.current) return;
      if (auth.observeSession(session).changed) cancel();
      setAuthRevision(value => value + 1);
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => update(session));
    const revision = auth.revision();
    void supabase.auth.getSession().then(result => {
      if (mounted.current && auth.revision() === revision) update(result.error ? null : result.data.session);
    }).catch(() => { if (mounted.current && auth.revision() === revision) update(null); });
    return () => {
      mounted.current = false; cancel(); auth.invalidate(); unsubscribeFocus(); subscription.unsubscribe();
      document.removeEventListener("visibilitychange", stopWhenHidden);
      window.removeEventListener("pagehide", cancel);
    };
  }, [cancel]);
  useLayoutEffect(() => { cancel(); }, [identity, props.enabled, cancel]);

  const start = async () => {
    const options = latest.current, owner = options.props.ownerGuard, auth = authScope.current.capture();
    if (!options.props.enabled || !owner || !auth || auth.ownerId !== owner.ownerId || !owner.isCurrent()
      || !voiceRef.current.supported || appAudioFocus.blocking() || document.visibilityState === "hidden") return;
    cancel();
    const run: CaptureRun = {
      identity: options.identity, auth, cancelled: false, stopping: false,
      guard: { ownerId: auth.ownerId, authSessionId: auth.authSessionId, isCurrent: () => false },
    };
    run.guard.isCurrent = () => current(run);
    runRef.current = run;
    setPhase("starting");
    try {
      const started = await voiceRef.current.startListening(run.guard);
      if (!current(run)) return;
      if (started) setPhase("recording");
      else { cancel(); setMessage("error"); }
    } catch {
      if (!current(run)) return;
      console.warn("[fitness-set-voice] requested capture unavailable");
      cancel(); setMessage("error");
    }
  };
  const review = useCallback(async () => {
    const run = runRef.current;
    if (!run || !current(run)) { cancel(); return; }
    if (run.stopping) return;
    run.stopping = true;
    setPhase("transcribing");
    try {
      const text = await voiceRef.current.stopListening(run.guard);
      if (!current(run)) return;
      const parsed = parseWorkoutSetVoice(text, latest.current.lang, latest.current.props.timed);
      if (!parsed) { cancel(); setMessage(text.trim() ? "notUnderstood" : "error"); return; }
      setDraft(parsed); setPhase("review");
    } catch {
      if (!current(run)) return;
      console.warn("[fitness-set-voice] requested transcription unavailable");
      cancel(); setMessage("error");
    }
  }, [cancel, current]);
  useEffect(() => {
    if (phase === "recording" && voice.recordingSeconds >= 15) void review();
  }, [phase, voice.recordingSeconds, review]);
  const apply = () => {
    const run = runRef.current;
    if (!run || !draft || !current(run) || appAudioFocus.blocking()) { cancel(); return; }
    latest.current.props.onApply(draft);
    cancel();
  };
  const active = phase === "starting" || phase === "recording" || phase === "transcribing";
  const blocked = blocker !== null && !(blocker === "capture" && active);
  const authIdentity = authScope.current.current();
  const unauthenticated = !authIdentity || authIdentity.ownerId !== props.ownerGuard?.ownerId;
  const format = (value: number) => new Intl.NumberFormat(localeTagFor(lang), { maximumFractionDigits: 2 }).format(value);
  return <section className="rounded-2xl border border-border bg-background/60 p-3" aria-label={t("fitness.setVoice.title")}>
    <p className="text-sm font-medium text-foreground">{t("fitness.setVoice.title")}</p>
    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t(props.timed ? "fitness.setVoice.exampleTimed" : "fitness.setVoice.exampleReps")}</p>
    <p className="mt-1 text-xs text-muted-foreground">{t("fitness.setVoice.limit")}</p>
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <button type="button" disabled={!props.enabled || !voice.supported || !authKnown || unauthenticated || blocked || phase === "starting" || phase === "transcribing"}
        onClick={() => void (phase === "recording" ? review() : start())}
        className="flex min-h-11 items-center gap-2 rounded-xl px-3 text-sm font-medium text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40">
        {phase === "starting" || phase === "transcribing" ? <Loader2 size={17} className="animate-spin" aria-hidden="true" />
          : phase === "recording" ? <Square size={16} aria-hidden="true" /> : <Mic size={17} aria-hidden="true" />}
        {t(phase === "recording" ? "fitness.setVoice.stop" : phase === "starting" || phase === "transcribing" ? "fitness.setVoice.processing" : "fitness.setVoice.start")}
      </button>
      {(active || draft) && <button type="button" onClick={cancel}
        className="flex min-h-11 items-center gap-1 rounded-xl px-3 text-sm text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
        <X size={16} aria-hidden="true" />{t("common.cancel")}
      </button>}
    </div>
    {phase === "recording" && <p role="status" className="mt-1 text-xs text-primary">{tf("fitness.setVoice.recording", { seconds: String(voice.recordingSeconds) })}</p>}
    {(message || !voice.supported || authKnown && unauthenticated || blocked) && <p role="status" className="mt-2 text-xs leading-relaxed text-muted-foreground">
      {t("fitness.setVoice." + (!voice.supported ? "unsupported" : unauthenticated ? "signIn" : blocked ? "audioUnavailable" : message))}
    </p>}
    {draft && <div className="mt-3 rounded-xl border border-primary/25 bg-primary/5 p-3">
      <p className="text-sm font-medium text-foreground">{t("fitness.setVoice.preview")}</p>
      <dl className="mt-2 flex flex-wrap gap-4 text-sm">
        {draft.weight !== undefined && <div><dt className="text-xs text-muted-foreground">{t("fitness.workout.weightLabel")}</dt><dd className="mt-1 font-semibold">{format(draft.weight)} {t("fitness.weightUnit")}</dd></div>}
        {draft.reps !== undefined && <div><dt className="text-xs text-muted-foreground">{t(props.timed ? "fitness.ux.seconds" : "fitness.workout.reps")}</dt><dd className="mt-1 font-semibold">{format(draft.reps)}</dd></div>}
      </dl>
      <button type="button" disabled={!props.enabled || blocked} onClick={apply}
        className="mt-3 min-h-11 w-full rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-40">
        {t("fitness.setVoice.apply")}
      </button>
    </div>}
    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{t("fitness.demo.completeHint")}</p>
  </section>;
}
